import React from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Group, Person } from '@mui/icons-material';
import { IUser } from '../../Interfaces/IUser';
import { IUserGroup, selectableUserGroups } from '../../Interfaces/IUserGroup';
import { ILegalTransactionType } from '../../Interfaces/ILegalTransactionType';
import { ILegalTransactionTypeSuggestion } from '../legalTransactionTypeMatcher';

export interface ITransactionHeaderValue {
  title: string;
  notaryIdUser: number | null;
  employeeIdUser: number | null;
  userGroupId: number | null;
  notarizationDate: string;
  recordNumbers: string[];
  transactionNumbers: string[];
  /** idLegalTransactionState (Status). Stammdaten — IDs systemweit gleich. */
  legalTransactionStateId: number | null;
  /** idLegalTransactionProgress (Vorgangsphase/Fortschritt). */
  legalTransactionProgressId: number | null;
}

export interface ILegalTransactionStateOption {
  idLegalTransactionState: number;
  LegalTransactionState: string;
  isDisabled?: boolean;
}

export interface ILegalTransactionProgressOption {
  idLegalTransactionProgress: number;
  LegalTransactionProgress: string;
}

interface IProps {
  /** 'transaction' = vollstaendiger Vorgang, 'clientsOnly' = nur Mandanten. */
  mode: 'transaction' | 'clientsOnly';
  onModeChange: (mode: 'transaction' | 'clientsOnly') => void;
  /** Hat die Importdatei ueberhaupt Vorgangsdaten? Wenn nein, dann sollte der
   *  Vorgangsmodus deaktiviert werden duerfen — wir blenden ihn nicht aus,
   *  damit der User trotzdem manuell einen Vorgang anlegen koennte. */
  hasTransactionContext: boolean;
  /** Wenn true, wird der Modus-Toggle ausgeblendet (z.B. beim DiNo-Vorgangs-
   *  Import, wo eine reine Mandanten-Anlage nie gewollt ist). */
  hideModeToggle?: boolean;

  // Vorgangsart
  legalTransactionTypes: ILegalTransactionType[] | undefined;
  idLegalTransactionType: number | '';
  setIdLegalTransactionType: (id: number | '') => void;
  transactionTypeHint: string | null;
  typeSuggestions: ILegalTransactionTypeSuggestion[];
  onUserPickedType: () => void;
  isLoadingLegalTransactionTypes: boolean;

  // Header-Felder
  value: ITransactionHeaderValue;
  onChange: (v: ITransactionHeaderValue) => void;

  users: IUser[];
  userGroups: IUserGroup[];
  legalTransactionStates: ILegalTransactionStateOption[];
  legalTransactionProgresses: ILegalTransactionProgressOption[];
  disabled?: boolean;
}

type AssigneeOption =
  | { type: 'Mitarbeiter'; id: number; name: string }
  | { type: 'Gruppe'; id: number; name: string };

export const TransactionHeaderSection: React.FC<IProps> = ({
  mode,
  onModeChange,
  hasTransactionContext,
  hideModeToggle,
  legalTransactionTypes,
  idLegalTransactionType,
  setIdLegalTransactionType,
  transactionTypeHint,
  typeSuggestions,
  onUserPickedType,
  isLoadingLegalTransactionTypes,
  value,
  onChange,
  users,
  userGroups,
  legalTransactionStates,
  legalTransactionProgresses,
  disabled,
}) => {
  const notaryOptions = (users ?? []).filter(
    u => Boolean(u.isNotary) && !u.isBlocked
  );

  const employeeOptions: AssigneeOption[] = [
    ...(users ?? [])
      .filter(u => !u.isNotary && !u.isBlocked)
      .map<AssigneeOption>(u => ({
        type: 'Mitarbeiter',
        id: u.idUser,
        name: `${u.FirstName ?? ''} ${u.LastName ?? ''}`.trim(),
      })),
    ...selectableUserGroups(userGroups).map<AssigneeOption>(g => ({
      type: 'Gruppe',
      id: g.idUserGroup,
      name: g.UserGroup,
    })),
  ];

  const selectedAssignee: AssigneeOption | null = value.employeeIdUser
    ? (employeeOptions.find(
        o => o.type === 'Mitarbeiter' && o.id === value.employeeIdUser
      ) ?? null)
    : value.userGroupId
      ? (employeeOptions.find(
          o => o.type === 'Gruppe' && o.id === value.userGroupId
        ) ?? null)
      : null;

  const set = (patch: Partial<ITransactionHeaderValue>) =>
    onChange({ ...value, ...patch });

  const isClientsOnly = mode === 'clientsOnly';

  const renderChipList = (
    field: 'recordNumbers' | 'transactionNumbers',
    label: string,
    helper: string
  ) => {
    const items = value[field];
    return (
      <Autocomplete
        multiple
        freeSolo
        size='small'
        options={[]}
        value={items}
        disabled={disabled}
        onChange={(_e, newVal) =>
          set({
            [field]: newVal as string[],
          } as Partial<ITransactionHeaderValue>)
        }
        renderTags={(values, getTagProps) =>
          values.map((v, idx) => (
            <Chip
              variant='outlined'
              label={v}
              size='small'
              {...getTagProps({ index: idx })}
              key={`${field}-${idx}`}
            />
          ))
        }
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            placeholder='Eintrag mit Enter bestätigen'
            helperText={helper}
          />
        )}
      />
    );
  };

  return (
    <Paper variant='outlined' sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent='space-between'
        spacing={1}
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography variant='subtitle2'>Vorgangsanlage</Typography>
          <Typography variant='caption' color='text.secondary'>
            {isClientsOnly
              ? 'Es werden ausschließlich die Mandanten in DiNo angelegt — kein Vorgang.'
              : 'Werte wurden – soweit möglich – aus der Importdatei vorbelegt.'}
          </Typography>
        </Box>
        {!hideModeToggle && (
          <FormControlLabel
            control={
              <Switch
                checked={isClientsOnly}
                disabled={disabled}
                onChange={(_, checked) =>
                  onModeChange(checked ? 'clientsOnly' : 'transaction')
                }
              />
            }
            label='Nur Mandanten anlegen'
          />
        )}
      </Stack>

      {isClientsOnly && (
        <Alert severity='info' sx={{ mb: 1 }}>
          {hasTransactionContext
            ? 'Beim Anlegen wird kein Vorgang erzeugt. Die Beteiligten unten werden als Mandanten angelegt bzw. mit bestehenden Mandanten verknüpft.'
            : 'Die Importdatei enthält keine Vorgangsdaten — es werden nur die Beteiligten als Mandanten in DiNo angelegt.'}
        </Alert>
      )}

      {!isClientsOnly && (
        <>
          <Divider sx={{ my: 1 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                size='small'
                fullWidth
                options={legalTransactionTypes ?? []}
                disabled={disabled || isLoadingLegalTransactionTypes}
                loading={isLoadingLegalTransactionTypes}
                value={
                  (legalTransactionTypes ?? []).find(
                    t => t.idLegalTransactionType === idLegalTransactionType
                  ) ?? null
                }
                getOptionLabel={o => o.LegalTransactionType ?? ''}
                isOptionEqualToValue={(opt, val) =>
                  opt.idLegalTransactionType === val.idLegalTransactionType
                }
                onChange={(_e, newValue) => {
                  setIdLegalTransactionType(
                    newValue?.idLegalTransactionType ?? ''
                  );
                  onUserPickedType();
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label='Vorgangsart'
                    placeholder='Suchen…'
                    error={!idLegalTransactionType}
                  />
                )}
                noOptionsText='Keine Treffer'
              />
              {transactionTypeHint && typeSuggestions.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    Vorschläge aus der Importdatei (Quelle:{' '}
                    <em>{transactionTypeHint}</em>):
                  </Typography>
                  <Stack
                    direction='row'
                    spacing={1}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    {typeSuggestions.map(s => {
                      const isActive =
                        s.type.idLegalTransactionType ===
                        idLegalTransactionType;
                      return (
                        <Chip
                          key={s.type.idLegalTransactionType}
                          size='small'
                          label={s.type.LegalTransactionType}
                          variant={isActive ? 'filled' : 'outlined'}
                          color={isActive ? 'primary' : 'default'}
                          onClick={
                            disabled
                              ? undefined
                              : () => {
                                  setIdLegalTransactionType(
                                    s.type.idLegalTransactionType
                                  );
                                  onUserPickedType();
                                }
                          }
                        />
                      );
                    })}
                  </Stack>
                </Box>
              )}
              {transactionTypeHint && typeSuggestions.length === 0 && (
                <Alert severity='info' sx={{ mt: 1 }}>
                  Importdatei nennt die Vorgangsart{' '}
                  <strong>{transactionTypeHint}</strong>. Auf diesem System
                  konnte keine passende Vorgangsart automatisch ermittelt werden
                  — bitte manuell auswählen.
                </Alert>
              )}
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                label='Bezeichnung'
                value={value.title}
                onChange={e => set({ title: e.target.value })}
                error={!value.title.trim()}
                size='small'
                fullWidth
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label='Beurkundungsdatum'
                type='date'
                value={value.notarizationDate}
                onChange={e => set({ notarizationDate: e.target.value })}
                size='small'
                fullWidth
                disabled={disabled}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label='Status'
                value={value.legalTransactionStateId ?? ''}
                onChange={e =>
                  set({
                    legalTransactionStateId:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                error={value.legalTransactionStateId == null}
                size='small'
                fullWidth
                select
                disabled={disabled}
              >
                {legalTransactionStates.map(s => (
                  <MenuItem
                    key={s.idLegalTransactionState}
                    value={s.idLegalTransactionState}
                    disabled={!!s.isDisabled}
                  >
                    {s.LegalTransactionState}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label='Phase'
                value={value.legalTransactionProgressId ?? ''}
                onChange={e =>
                  set({
                    legalTransactionProgressId:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                error={value.legalTransactionProgressId == null}
                size='small'
                fullWidth
                select
                disabled={disabled}
              >
                {legalTransactionProgresses.map(p => (
                  <MenuItem
                    key={p.idLegalTransactionProgress}
                    value={p.idLegalTransactionProgress}
                  >
                    {p.LegalTransactionProgress}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label='Zugewiesener Notar'
                value={value.notaryIdUser ?? ''}
                onChange={e =>
                  set({
                    notaryIdUser:
                      e.target.value === '' ? null : Number(e.target.value),
                  })
                }
                error={value.notaryIdUser === null}
                size='small'
                fullWidth
                select
                disabled={disabled}
              >
                <MenuItem value=''>
                  <em>–</em>
                </MenuItem>
                {notaryOptions.map(u => (
                  <MenuItem key={u.idUser} value={u.idUser}>
                    {u.FirstName} {u.LastName}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={employeeOptions}
                groupBy={o => o.type}
                getOptionLabel={o => o.name}
                size='small'
                disabled={disabled}
                value={selectedAssignee}
                onChange={(_e, newValue) => {
                  if (!newValue) {
                    set({ employeeIdUser: null, userGroupId: null });
                  } else if (newValue.type === 'Mitarbeiter') {
                    set({ employeeIdUser: newValue.id, userGroupId: null });
                  } else {
                    set({ employeeIdUser: null, userGroupId: newValue.id });
                  }
                }}
                renderOption={(props, option) => (
                  <ListItem {...props} key={`${option.type}-${option.id}`}>
                    <ListItemText primary={option.name} />
                  </ListItem>
                )}
                renderInput={params => (
                  <TextField
                    {...params}
                    size='small'
                    label={
                      <Box component='span'>
                        {selectedAssignee?.type === 'Gruppe' ? (
                          <>
                            <Group sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Zugewiesene Gruppe
                          </>
                        ) : (
                          <>
                            <Person sx={{ verticalAlign: 'middle', mr: 1 }} />
                            Zugewiesener Mitarbeiter
                          </>
                        )}
                      </Box>
                    }
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              {renderChipList(
                'recordNumbers',
                'Urkundennummer(n)',
                'Eingaben mit Enter trennen; mehrere möglich.'
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderChipList(
                'transactionNumbers',
                'Aktenzeichen',
                'Eingaben mit Enter trennen; mehrere möglich.'
              )}
            </Grid>
          </Grid>
        </>
      )}
    </Paper>
  );
};
